import { Box, Button, Card, CardBody, CardHeader, CheckBox, FormField, Heading, Image, Markdown, Notification, RadioButtonGroup, Text, TextInput } from "grommet";
import { Clear, Connect, Detach, Download, Share } from "grommet-icons";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { formattedPrice } from "../../utils/priceUtils";
import { useLazyGetMonobankJarsQuery, useSetupMonobankWebhookMutation } from "../../api/monobankApi";
import { useUpdatePaymentOptionMutation } from "../../api/paymentOptionsApi";
import { useDispatch } from "react-redux";
import { addNotification } from "../../slices/notificationsSlice";
import { InfoSection } from "../layout/InfoSection";
import { MonobankTipsTranslation } from "../../md-translation/MonobankTipsTranslation";

export const MonobankPaymentMethodCard = ({ title, sendId, enabled, helpLink }) => {

    const { t, i18n, ready } = useTranslation('monobank-settings', {
		useSuspense: false,
	});

    const dispatch = useDispatch();

    const [getMonobankJars, { isSuccess: isJarsReceived }] = useLazyGetMonobankJarsQuery();
    const [setupMonobankWebhook] = useSetupMonobankWebhookMutation();
    const [updatePaymentOption] = useUpdatePaymentOptionMutation();

    const [monoToken, setMonoToken] = useState();
    const [monobankJars, setMonobankJars] = useState([]);
    const [selectedJar, setSelectedJar] = useState();

    const [jarOptions, setJarOptions] = useState([]);

    const handleReceiveMonobankJars = () => {
        if (!monoToken) return;
        getMonobankJars({ token: monoToken })
            .then(response => {
                const info = response.data;

                const jars = info?.jars;

                if (jars) {
                    setMonobankJars(jars);
                }

            });
    }

    const handleJarSelect = (event) => {
        const jarId = event.value;
        setSelectedJar(jarId);
    }

    const handleConnectJar = () => {
        const jar = monobankJars.filter(jar => jar.id === selectedJar)?.pop();

        updatePaymentOption({
            type: 'MONOBANK',
            sendId: jar.sendId,
            jarId: jar.jarId,
            title: jar.title,
        })
        .unwrap()
        .then(response => {
            setMonobankJars([]);
            setJarOptions([]);
            const webhookToken = response?.webhookToken;
            setupMonobankWebhook({ token: monoToken, body: { webHookUrl: `${process.env.REACT_APP_WEBSOCKET_URL}/monobank/personal/webhook/${webhookToken}` } })
            .unwrap()
            .then(resp => dispatch(addNotification({ show: true, message: `Monobank jar sucessfully connected.`, status: 'normal' })))
            .catch(error => dispatch(addNotification({ show: true, message: 'Unable to connect monobank jar.', status: 'critical' })))
        })
        .catch(error => dispatch(addNotification({ show: true, message: 'Unable to connect monobank jar.', status: 'critical' })))
    }

    const handleDisconnectJar = () => {
        updatePaymentOption({
            type: 'MONOBANK',
            sendId: '',
            title: '',
        }).unwrap()
        .then(response => dispatch(addNotification({ show: true, message: `Monobank jar sucessfully disconnected.`, status: 'normal' })))
        .catch(error => dispatch(addNotification({ show: true, message: 'Unable to disconnect monobank jar.', status: 'critical' })))
    }

    const handleCancelJar = () => {
        setMonobankJars([]);
        setJarOptions([]);
    }

    const handleEnabledToggle = (value) => {
        updatePaymentOption(value)
        .unwrap()
        .then(response => dispatch(addNotification({ show: true, message: `Payment option ${response?.enabled ? 'enabled' : 'disabled'}.`, status: 'normal' })))
        .catch(error => dispatch(addNotification({ show: true, message: `Unable to ${value.enabled ? 'enabled' : 'disabled'} payment option.`, status: 'critical' }))) 
    }

    useEffect(() => {
        if (monobankJars?.length > 0) {
            const jarOptions = monobankJars.map(jar => ({
                disabled: false,
                id: jar.id,
                label:  <MonobankaPreview title={jar.title} balance={jar.balance} goal={jar.goal} />,
                value: jar.id
            }));

            setJarOptions(jarOptions);
            setSelectedJar(jarOptions[0].id)
        }
    }, [monobankJars])
    
    return (
        <Card>
            <CardHeader pad={{ top: 'medium', left: 'medium', right: 'medium' }}>
                <Box direction="row" gap="small" align="end">
                    <Image height={'30px'} width={'30px'} src={`${process.env.PUBLIC_URL}/images/monobank-logo.png`} /><Heading margin={{ top: 'none', bottom: 'none' }} level={2} size="medium" weight={'bold'}>{t('title')}</Heading>
                </Box>
                <CheckBox toggle pad={'none'} checked={enabled} onClick={(event) => handleEnabledToggle({ type: 'MONOBANK', enabled: event.target.checked })} />
            </CardHeader>
            <CardBody direction="row-responsive" gap="medium" responsive>
                <Box basis="50%" direction="row" border={{ color: 'background-contrast', side: 'top', size: 'small' }}>
                    <Box direction="column" margin={{ top: 'medium' }} width={'large'}>
                        {!title && !sendId && <Box direction="column" gap="small" margin={{ bottom: 'small' }}>
                            <FormField label={t('monobankApiTokenTitle')}>
                                <TextInput type="password" value={monoToken} onChange={(event) => setMonoToken(event.target.value)} />
                            </FormField>
                            <Button icon={<Download />} label={t('receiveMonoJarsButton')} secondary onClick={handleReceiveMonobankJars} />
                        </Box>}
                        {!title && !sendId && <Notification message={t('monoApiDisclaimer')} actions={[{ label: t('monoApiDisclaimerLinkTitle'), href: helpLink, target: '_blank' }]} status="info" />}
                        {jarOptions?.length > 0 && <Box direction="column" gap="small" margin={{ bottom: 'medium' }}>
                            <RadioButtonGroup value={selectedJar} options={jarOptions} onChange={handleJarSelect} />
                            <Box direction="row" gap="small">
                                <Button icon={<Clear/>} label={t('cancelButton')} justify="center" onClick={handleCancelJar} />
                                <Button icon={<Connect/>} label={t('connectButton')} secondary justify="center" onClick={handleConnectJar} />
                            </Box>
                        </Box>}
                        {!title && !sendId && isJarsReceived && jarOptions?.length === 0 && <Box margin={{ top: 'small' }}>
                            <Notification title="No jars found" status="warning" />
                        </Box>}
                        {/* <Notification message="Unable to find any jars. Create a jar in Monobank app and try again." status="warning" /> */}
                        {sendId && title && <Box direction="row" justify="between" align="center" round="xsmall" border={{ color: '#ffffff5c', style: 'solid', size: '1px' }} pad={'small'} >
                            <Text size="medium" weight={'bold'}>{title}</Text>
                            <Box direction="column" gap="small">
                                <Button icon={<Share />} label={t('openJarButton')} justify="center" href={`https://send.monobank.ua/${sendId}`} target="_blank" />
                                <Button icon={<Detach />} label={t('disconnectJarButton')} secondary justify="center" onClick={handleDisconnectJar} />
                            </Box>
                        </Box>}
                    </Box>
                </Box>
                <Box basis="50%">
                    <InfoSection>
                        <Box>
                            <Markdown components={{ p: { props: { style: { maxWidth: '100%' } } }, img: { props: { style: { width: '100%' } } } }}>{MonobankTipsTranslation[i18n.language]}</Markdown>
                        </Box>
                    </InfoSection>
                </Box>
            </CardBody>
        </Card>
    )
}

const MonobankaPreview = ({ title, balance, goal }) => {

    return (
        <Box direction="column" gap="small" margin={{ top: 'xsmall', bottom: 'xsmall' }}>
            <Text size="small" weight={'bold'}>{title}</Text>
            <Box direction="column" gap="xsmall">
                <Text size="xsmall" weight={400}><Trans i18nKey="monobank-settings:balance" values={{ balance: formattedPrice(balance) }}/></Text>
                {goal && <Text size="xsmall" weight={400}><Trans i18nKey="monobank-settings:goal" values={{ goal: formattedPrice(goal) }}/></Text>}
            </Box>
        </Box>
    )
}